/**
 * Reader reviews on a book's page (issue #89).
 *
 * Published at once. Every review can be reported by anyone, and removed by
 * the maintainer, signed in as himself, from this same page on a phone: the
 * reason is required and the author is told it by email. A review marked as
 * containing spoilers is folded until the reader opens it.
 *
 * The list is rendered by the server with the page (lib/seed.ts), so search
 * engines index it, a choice the maintainer made knowingly. The browser then
 * asks again, to learn which review is the reader's own and whether they may
 * remove reviews: things the server did not render for.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation } from 'react-router'
import type { MessageKey } from '../../shared/message-keys.ts'
import { signInPath } from '../../shared/return-path.ts'
import {
  REASON_MAX_LENGTH,
  REVIEW_MAX_LENGTH,
  removeInputSchema,
  reviewInputSchema,
  suspensionEnds,
  type PublicReview,
  type ReviewsResponse,
  type WritingState,
} from '../../shared/reviews.ts'
import { useI18n } from '../i18n/index.tsx'
import { authClient } from '../lib/auth-client.ts'
import { fill } from '../lib/catalogue.ts'
import { useSeededReviews } from '../lib/seed.ts'
import { reviewsBase as base, reviewsChanged, send, useReviewsChanged } from '../lib/reviews-api.ts'
import { useClientConfig } from '../lib/use-client-config.ts'
import { Alert } from './Alert.tsx'
import { CharacterCount } from './CharacterCount.tsx'
import { Turnstile } from './Turnstile.tsx'

export function Reviews({ world, work }: { world: string; work: string }) {
  const { t } = useI18n()
  const { data: session } = authClient.useSession()
  const signedIn = session !== null && session !== undefined && session.user.emailVerified
  const seededReviews = useSeededReviews(world, work)
  const [state, setState] = useState<ReviewsResponse>(() => ({ reviews: seededReviews ?? [], moderator: false, writing: null }))
  const [notice, setNotice] = useState<MessageKey | null>(null)

  const reload = useCallback(async () => {
    const result = await send('GET', base(world, work))
    if (result.ok) setState(result.data as ReviewsResponse)
  }, [world, work])

  // Again in the browser, and whenever the reader signs in or out: which
  // review is theirs and whether they moderate depend on who is asking.
  useEffect(() => {
    let live = true
    void send('GET', base(world, work)).then((result) => {
      if (live && result.ok) setState(result.data as ReviewsResponse)
    })
    return () => {
      live = false
    }
  }, [world, work, signedIn])

  // A note published from the shelf panel creates or extends the reader's review.
  const onChanged = useCallback(() => {
    void reload()
  }, [reload])
  useReviewsChanged(world, work, onChanged)

  const mine = state.reviews.find((review) => review.mine) ?? null
  const others = state.reviews

  return (
    <section className="reviews" aria-labelledby="reviews-heading">
      <h2 id="reviews-heading" className="label">
        {t('reviews.title')}
      </h2>
      {signedIn ? <OwnReview world={world} work={work} mine={mine} writing={state.writing} onChange={reload} /> : <SignedOut />}
      <p className="reviews__notice" role="status">
        {notice === null ? null : t(notice)}
      </p>
      {others.length === 0 ? (
        <p className="reviews__empty">{t('reviews.none')}</p>
      ) : (
        <ol className="reviews__list">
          {others.map((review) => (
            <ReviewItem
              key={review.id}
              world={world}
              review={review}
              moderator={state.moderator}
              onRemoved={async (notified) => {
                setNotice(notified ? 'reviews.remove.done' : 'reviews.remove.done_unnotified')
                await reload()
              }}
            />
          ))}
        </ol>
      )}
    </section>
  )
}

function SignedOut() {
  const { t } = useI18n()
  const { pathname } = useLocation()
  return (
    <p className="reviews__signin">
      <Link to={signInPath(pathname)}>{t('reviews.signin')}</Link>
    </p>
  )
}

function OwnReview({
  world,
  work,
  mine,
  writing,
  onChange,
}: {
  world: string
  work: string
  mine: PublicReview | null
  writing: WritingState | null
  onChange: () => Promise<void>
}) {
  const { t, locale } = useI18n()
  const [open, setOpen] = useState(false)
  const [body, setBody] = useState(mine?.body ?? '')
  const [spoilers, setSpoilers] = useState(mine?.spoilers ?? false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<MessageKey | null>(null)

  // Suspended or out of reviews (the strike rule, shared/reviews.ts): no form,
  // a plain statement of why and until when, and a reader's own review can
  // still be deleted, because deletion is never taken away.
  if (writing !== null && writing.state !== 'allowed') {
    return (
      <div className="reviews__own">
        <p className="reviews__blocked">
          {writing.state === 'suspended' ? fill(t('reviews.suspended'), { date: suspensionEnds(locale, writing.until) }) : t('reviews.banned')}
        </p>
        {mine === null ? null : (
          <button type="button" className="link-button" disabled={busy} onClick={() => void onDelete()}>
            {t('reviews.delete')}
          </button>
        )}
        {error ? <Alert tone="error">{t(error)}</Alert> : null}
      </div>
    )
  }

  if (!open) {
    return (
      <p className="reviews__own">
        <button
          type="button"
          className="button"
          onClick={() => {
            setBody(mine?.body ?? '')
            setSpoilers(mine?.spoilers ?? false)
            setOpen(true)
          }}
        >
          {t(mine === null ? 'reviews.write' : 'reviews.edit')}
        </button>
      </p>
    )
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    const parsed = reviewInputSchema.safeParse({ body, spoilers })
    if (!parsed.success) {
      setError((parsed.error.issues[0]?.message ?? 'error.bad_request') as MessageKey)
      return
    }
    setBusy(true)
    const result = await send('PUT', base(world, work), parsed.data)
    setBusy(false)
    if (!result.ok) {
      setError(result.key)
      return
    }
    setOpen(false)
    await onChange()
    reviewsChanged(world, work)
  }

  async function onDelete() {
    setBusy(true)
    const result = await send('DELETE', base(world, work))
    setBusy(false)
    if (!result.ok) {
      setError(result.key)
      return
    }
    setOpen(false)
    await onChange()
    reviewsChanged(world, work)
  }

  return (
    <form className="reviews__form" onSubmit={(event) => void onSubmit(event)} noValidate>
      <div className="field">
        <label className="field__label" htmlFor="review-body">
          {t('reviews.body.label')}
        </label>
        <p className="field__hint" id="review-body-hint">
          {t('reviews.body.hint')}
        </p>
        <textarea
          id="review-body"
          className="field__input field__input--area"
          rows={6}
          maxLength={REVIEW_MAX_LENGTH}
          aria-describedby="review-body-hint review-body-count"
          value={body}
          onChange={(event) => {
            setBody(event.target.value)
          }}
        />
        <CharacterCount id="review-body-count" count={body.length} limit={REVIEW_MAX_LENGTH} />
      </div>
      <label className="shelf__choice">
        <input
          type="checkbox"
          checked={spoilers}
          onChange={(event) => {
            setSpoilers(event.target.checked)
          }}
        />
        <span>{t('reviews.spoilers.label')}</span>
      </label>
      {error ? <Alert tone="error">{t(error)}</Alert> : null}
      <p className="reviews__actions">
        <button type="submit" className="button" disabled={busy}>
          {t('reviews.publish')}
        </button>
        <button
          type="button"
          className="link-button"
          onClick={() => {
            setOpen(false)
          }}
        >
          {t('reviews.cancel')}
        </button>
        {mine === null ? null : (
          <button type="button" className="link-button" disabled={busy} onClick={() => void onDelete()}>
            {t('reviews.delete')}
          </button>
        )}
      </p>
    </form>
  )
}

function ReviewItem({
  world,
  review,
  moderator,
  onRemoved,
}: {
  world: string
  review: PublicReview
  moderator: boolean
  onRemoved: (notified: boolean) => Promise<void>
}) {
  const { t, locale } = useI18n()
  const date = new Intl.DateTimeFormat(locale === 'bg' ? 'bg-BG' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Sofia',
  }).format(new Date(review.created_at))
  const [panel, setPanel] = useState<'none' | 'report' | 'remove'>('none')
  const text = <p className="reviews__body">{review.body}</p>

  return (
    <li className="reviews__item" id={`review-${String(review.id)}`}>
      <p className="reviews__meta">
        <span className="reviews__author">{review.author ?? t('reviews.anonymous')}</span>
        {review.mine ? <span className="reviews__mine"> · {t('reviews.yours')}</span> : null}
        <span className="reviews__date"> · {date}</span>
      </p>
      {review.spoilers ? (
        <details className="reviews__spoilers">
          <summary>{t('reviews.spoilers.show')}</summary>
          {text}
        </details>
      ) : (
        text
      )}
      <p className="reviews__controls">
        {review.mine ? null : (
          <button
            type="button"
            className="link-button"
            aria-expanded={panel === 'report'}
            onClick={() => {
              setPanel(panel === 'report' ? 'none' : 'report')
            }}
          >
            {t('reviews.report')}
          </button>
        )}
        {moderator ? (
          <button
            type="button"
            className="link-button reviews__remove"
            aria-expanded={panel === 'remove'}
            onClick={() => {
              setPanel(panel === 'remove' ? 'none' : 'remove')
            }}
          >
            {t('reviews.remove')}
          </button>
        ) : null}
      </p>
      {panel === 'report' ? <ReportForm world={world} id={review.id} /> : null}
      {panel === 'remove' ? <RemoveForm world={world} id={review.id} onRemoved={onRemoved} /> : null}
    </li>
  )
}

/** Anyone may report: an optional reason and Turnstile, and the maintainer is emailed at once. */
function ReportForm({ world, id }: { world: string; id: number }) {
  const { t, locale } = useI18n()
  const config = useClientConfig()
  const [reason, setReason] = useState('')
  const [token, setToken] = useState('')
  const [resets, setResets] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<MessageKey | null>(null)
  const [done, setDone] = useState(false)

  if (done) return <p role="status">{t('reviews.report.done')}</p>

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (token === '') {
      setError('error.turnstile_missing')
      return
    }
    setBusy(true)
    const result = await send('POST', `/api/reviews/${encodeURIComponent(world)}/report/${String(id)}`, { reason, turnstileToken: token })
    setBusy(false)
    setToken('')
    setResets((n) => n + 1)
    if (!result.ok) setError(result.key)
    else setDone(true)
  }

  return (
    <form className="reviews__panel" onSubmit={(event) => void onSubmit(event)} noValidate>
      <div className="field">
        <label className="field__label" htmlFor={`report-${String(id)}`}>
          {t('reviews.report.reason')}
        </label>
        <textarea
          id={`report-${String(id)}`}
          className="field__input field__input--area"
          rows={3}
          maxLength={REASON_MAX_LENGTH}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value)
          }}
        />
      </div>
      {config ? <Turnstile siteKey={config.turnstileSiteKey} locale={locale} onToken={setToken} resetSignal={resets} /> : null}
      {error ? <Alert tone="error">{t(error)}</Alert> : null}
      <button type="submit" className="button" disabled={busy}>
        {t('reviews.report.send')}
      </button>
    </form>
  )
}

/** The maintainer only: a required reason, which the author receives by email. */
function RemoveForm({ world, id, onRemoved }: { world: string; id: number; onRemoved: (notified: boolean) => Promise<void> }) {
  const { t } = useI18n()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<MessageKey | null>(null)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    const parsed = removeInputSchema.safeParse({ reason })
    if (!parsed.success) {
      setError((parsed.error.issues[0]?.message ?? 'error.reason_required') as MessageKey)
      return
    }
    setBusy(true)
    const result = await send('POST', `/api/reviews/${encodeURIComponent(world)}/remove/${String(id)}`, parsed.data)
    setBusy(false)
    if (!result.ok) {
      setError(result.key)
      return
    }
    await onRemoved((result.data as { notified?: boolean }).notified !== false)
  }

  return (
    <form className="reviews__panel reviews__panel--remove" onSubmit={(event) => void onSubmit(event)} noValidate>
      <div className="field">
        <label className="field__label" htmlFor={`remove-${String(id)}`}>
          {t('reviews.remove.reason')}
        </label>
        <p className="field__hint">{t('reviews.remove.hint')}</p>
        <textarea
          id={`remove-${String(id)}`}
          className="field__input field__input--area"
          rows={3}
          maxLength={REASON_MAX_LENGTH}
          required
          value={reason}
          onChange={(event) => {
            setReason(event.target.value)
          }}
        />
      </div>
      {error ? <Alert tone="error">{t(error)}</Alert> : null}
      <button type="submit" className="button button--danger" disabled={busy}>
        {t('reviews.remove.confirm')}
      </button>
    </form>
  )
}
